import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery, useQueryClient } from 'react-query';

import { getCertificateURL, getConfirmationPageData } from '../../../api/confirmation/confirmation';
import {
  BillingDetailsWithCardInfoI,
  CertificateI,
  SelectedItemsI,
} from '../../../declarations/interfaces/confirmation';

const QUERY_KEYS = {
  confirmationData: 'confirmation-data',
  certificateUrls: 'certificate-urls',
};

const useConfirmation = (confirmationId: string) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const [billingDetails, setBillingDetails] = useState({} as BillingDetailsWithCardInfoI);
  const [selectedItems, setSelectedItems] = useState({} as SelectedItemsI);
  const [certificateUrl, setCertificateUrl] = useState('');
  const [socialTitle, setSocialTitle] = useState('');
  const [popupTitle, setPopupTitle] = useState('');
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [certificateData, setCertificateData] = useState({} as CertificateI);

  const { isLoading, data: confirmationData } = useQuery(
    [QUERY_KEYS.confirmationData],
    () => getConfirmationPageData(confirmationId),
    {
      select: data => data.data,
      onSuccess(data) {
        setBillingDetails(data.billingDetailsWithCardInfo);
        setSelectedItems(data.selectedItems);

        setConfirmationNumber(data.certificateId.slice(-10));
      },
    }
  );

  const { refetch: getCertificate } = useQuery(
    [[QUERY_KEYS.certificateUrls]],
    () => getCertificateURL(confirmationData?.certificateId || ''),
    {
      enabled: false,
      select: data => data.data,
      onSuccess(data) {
        setCertificateData(data);
      },
    }
  );

  const handleShowCertificate = async (type: 'cop' | 'sa') => {
    const reference =
      confirmationData?.certificateId || `${billingDetails.firstName} ${billingDetails.lastName}`;

    let certData = certificateData;

    if (!certData || !Object.keys(certData).length) {
      const res = await getCertificate();

      certData = res.data as CertificateI;
    }

    if (type === 'cop') {
      setCertificateUrl(certData?.copCertURL || '');
      setSocialTitle(`Carbon Offset Project Certificate presented to ${reference}`);
      setPopupTitle(
        formatMessage({
          id: 'certificateDialog.title',
          defaultMessage: 'Certificate of Carbon Footprint Offset',
        })
      );

      return;
    }

    setCertificateUrl(certData?.saCertURL || '');
    setSocialTitle(`Sustainability Action Certificate presented to ${reference}`);
    setPopupTitle(
      formatMessage({
        id: 'certificateDialog.sustainableAction.title',
        defaultMessage: 'Certificate of Sustainable Actions',
      })
    );
  };

  useEffect(() => {
    return () => queryClient.removeQueries(QUERY_KEYS.confirmationData);
  }, []);

  return {
    isLoading,
    billingDetails,
    selectedItems,
    certificateUrl,
    socialTitle,
    popupTitle,
    confirmationNumber,
    getCertificate,
    handleShowCertificate,
  };
};

export default useConfirmation;
